import React, { useState, useRef } from "react";
import { videoShowcases } from "../mock";
import ContactForm from "../components/heider/ContactForm";
import Footer from "../components/heider/Footer";
import useReveal from "../hooks/useReveal";
import { Play, Pause, ArrowRight, Volume2, VolumeX } from "lucide-react";
import ScrollCue from "../components/heider/ScrollCue";

const ShowcaseCard = ({ item }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.muted = muted; v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (v) v.muted = !muted;
    setMuted(!muted);
  };
  return (
    <div className="reveal-scale group">
      <div className="relative overflow-hidden bg-black aspect-[16/10]">
        <video ref={ref} className="w-full h-full object-cover" poster={item.poster} loop playsInline onClick={toggle}>
          <source src={item.video} type="video/mp4" />
        </video>
        <button onClick={toggle} data-testid={`showcase-play-${item.id}`} className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${playing ? "opacity-0 hover:opacity-100" : "opacity-100"}`} aria-label="Play">
          <span className="w-16 h-16 rounded-full bg-white/90 text-black flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
            {playing ? <Pause size={22} /> : <Play size={22} className="ml-1" />}
          </span>
        </button>
        <button onClick={toggleMute} data-testid={`showcase-mute-${item.id}`} aria-label={muted ? "Unmute" : "Mute"} className="absolute bottom-4 right-4 z-10 w-11 h-11 rounded-full bg-black/60 backdrop-blur-md border border-white/30 text-white flex items-center justify-center hover:bg-black/80 transition-colors">
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      </div>
      <div className="pt-5">
        <h3 className="font-serif-display text-2xl text-white">{item.name}</h3>
        <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mt-1">{item.status}</p>
      </div>
    </div>
  );
};

const SellPage = () => {
  useReveal();
  return (
    <div className="page-in">
      {/* Video Hero */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden bg-black">
        <video className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline poster="https://images.pexels.com/photos/7045701/pexels-photo-7045701.jpeg?auto=compress&cs=tinysrgb&w=1600">
          <source src="/home2.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative text-center text-white px-6">
          <h1 className="font-serif-display font-light text-5xl md:text-7xl lg:text-[6rem] leading-[1.05] clip-line">
            <span style={{ opacity: 0 }}>Your Perfect Buyer is Out There</span>
          </h1>
          <p className="mt-6 text-sm md:text-base tracking-luxe uppercase text-white/85 fade-in" style={{ animationDelay: "0.6s", opacity: 0 }}>We Will Find Them</p>
        </div>
        <ScrollCue />
      </section>

      {/* Commanding results */}
      <section className="bg-[#f3f1ec] py-24 md:py-32">
        <div className="max-w-[900px] mx-auto px-6 text-center reveal">
          <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1a1a1a] mb-8">Commanding Results. Uncompromising Service.</h2>
          <p className="text-neutral-500 text-[15px] leading-relaxed">
            When it comes to one of your most significant investments, relying on an automated estimate simply isn’t enough. At CAPITAL CORNER, we offer a complimentary market assessment grounded in real-time data, deep local expertise, and an understanding of what truly sets your property apart. Share a few key details with us, and our team will deliver an accurate, insightful valuation — along with the guidance you need to make confident decisions.
          </p>
          <a href="#contact" className="inline-flex items-center gap-2 mt-10 text-[11px] tracking-luxe uppercase text-[#1a1a1a] border border-[#1a1a1a] px-8 py-4 hover:bg-[#1a1a1a] hover:text-white transition-colors">
            Discover Your Home's Market Value <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* Broker Opens */}
      <section className="relative overflow-hidden bg-[#111113] text-white py-24 md:py-32">
        <img src="https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Broker open event" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative max-w-[1000px] mx-auto px-6 md:px-10 text-center reveal">
          <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-4">From Digital Buzz to Real-World Demand</p>
          <h2 className="font-serif-display text-4xl md:text-6xl font-light leading-tight">Broker Opens Redefined</h2>
          <div className="w-40 h-px bg-[#b4936a] mx-auto my-10" />
          <p className="text-white/60 text-[15px] leading-relaxed max-w-2xl mx-auto">
            As part of our signature 3-point marketing strategy, we combine massive online exposure, premium listing presentation, and unforgettable in-person events to create demand around every property. From live DJs and elevated catering to exotic cars, luxury staging, and curated experiences &mdash; our broker opens turn your home into the event everyone is talking about.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-14">
            {[
              { v: "Millions", l: "Views Generated Online" },
              { v: "Top Agents", l: "& Qualified Buyers In-Person" },
              { v: "Zillow", l: "Showcase Partnership" },
            ].map((s, i) => (
              <div key={i} className="border-t border-white/15 pt-6">
                <div className="font-serif-display text-3xl md:text-4xl mb-2">{s.v}</div>
                <div className="text-white/50 text-[10px] tracking-luxe uppercase">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video showcases */}
      <section className="bg-[#0d0d0d] text-white py-24 md:py-32">
        <div className="max-w-[1700px] mx-auto px-6 md:px-10">
          <div className="text-center mb-16 reveal">
            <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-4">Cinematic Marketing</p>
            <h2 className="font-serif-display text-4xl md:text-6xl font-light">Video Showcases</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-[1300px] mx-auto" data-testid="video-showcases">
            {videoShowcases.map((item) => (
              <ShowcaseCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </div>
  );
};

export default SellPage;
